import React, { useState, useMemo, useEffect, useRef } from 'react';
import css from './SearchResultsPanel.module.css';
import { ListingCard } from '../../../components';

const arroLeft = 'https://res.cloudinary.com/dlcacck3a/image/upload/v1769082515/socialbnb/assets/icons/arrows/Vector_2.svg';

const arroRight = 'https://res.cloudinary.com/dlcacck3a/image/upload/v1769082513/socialbnb/assets/icons/arrows/Vector_1.svg';

const cardRenderSizes = [
  '(max-width: 767px) 100vw',
  `(max-width: 1023px) ${50}vw`,
  `(max-width: 1920px) ${62.5 / 2}vw`,
  `${62.5 / 3}vw`,
].join(', ');

// Read the numeric price amount (Money.amount) from a listing, or null when
// it has no usable price so it can be pushed to the end of the group.
const getPriceAmount = item => {
  const amount = item?.attributes?.price?.amount;
  return typeof amount === 'number' ? amount : null;
};

// When the user sorts the search by price, the listings inside a grouped card
// should follow the same direction so the first shown slide (and its badge)
// matches the active sort. `descending` = "Highest price" first.
const sortByPrice = (objects, descending) => {
  return objects?.sort((a, b) => {
    const aPrice = getPriceAmount(a);
    const bPrice = getPriceAmount(b);
    // Listings without a price go last regardless of direction.
    if (aPrice == null && bPrice == null) return 0;
    if (aPrice == null) return 1;
    if (bPrice == null) return -1;
    return descending ? bPrice - aPrice : aPrice - bPrice;
  });
};

const sortByBoostAndRating = objects => {
  return objects?.sort((a, b) => {
    // Primary sort: boostScore (descending)
    const aBoostScore = a?.attributes?.publicData?.boostScore || 0;
    const bBoostScore = b?.attributes?.publicData?.boostScore || 0;
    if (bBoostScore !== aBoostScore) {
      return bBoostScore - aBoostScore;
    }

    // Secondary sort: rating (descending)
    const getRating = item => {
      const publicData = item?.attributes?.publicData || {};
      const finalReviews = publicData.finalReviews || { count: 0, rating: 0 };
      const googleReviews = publicData.location?.reviews || publicData.googleReviews || [];
      if (finalReviews.count > 0 && finalReviews.rating > 0) {
        return finalReviews.rating;
      }

      if (googleReviews.length > 0) {
        const totalRating = googleReviews.reduce((sum, review) => sum + (review.rating || 0), 0);
        const averageRating = totalRating / googleReviews.length;
        return Math.round(averageRating * 10) / 10;
      }

      return 0;
    };

    const aRating = getRating(a);
    const bRating = getRating(b);

    return bRating - aRating;
  });
};

export default function Cards({
  array,
  length,
  setActiveListing,
  isSearchPage = true,
  onSlideListingChange,
  sort,
}) {
  const [current, setCurrent] = useState(0);

  // When a price sort is active, order the slides by price in that direction so
  // the group matches the active search sort. Otherwise fall back to the default
  // boost/rating ordering. (`price` = highest first, `-price` = lowest first.)
  const isPriceSort = sort === 'price' || sort === '-price';
  const sortGroup = list =>
    isPriceSort ? sortByPrice(list, sort === 'price') : sortByBoostAndRating(list);
  // Redux often replaces `listings` with a new array reference on each store update.
  // Key effects off stable listing ids so we do not reset the slider or fire slide
  // callbacks on every parent re-render (that broke search pagination / navigation).
  const listingIdsKey = useMemo(
    () =>
      (array || [])
        .map(x => x?.id?.uuid)
        .filter(Boolean)
        .join('|'),
    [array]
  );

  const sortedSlides = useMemo(() => {
    const copy = [...(array || [])];
    return sortGroup(copy);
  }, [array, isPriceSort, sort]);

  const arrayRef = useRef(array);
  arrayRef.current = array;

  useEffect(() => {
    setCurrent(0);
  }, [listingIdsKey]);

  useEffect(() => {
    if (!onSlideListingChange) {
      return;
    }
    const copy = [...(arrayRef.current || [])];
    const sorted = sortGroup(copy);
    const active = sorted[current];
    if (active) {
      onSlideListingChange(active);
    }
  }, [current, listingIdsKey, onSlideListingChange, isPriceSort, sort]);

  const nextSlide = () => {
    setCurrent(current === sortedSlides.length - 1 ? 0 : current + 1);
  };

  const prevSlide = () => {
    setCurrent(current === 0 ? sortedSlides.length - 1 : current - 1);
  };

  const activeSlide = sortedSlides[current];

  if (!sortedSlides.length) {
    return null;
  }

  return (
    <section className={css.cards_slider}>
      {sortedSlides.map((slide, index) => {
        return (
          <div className={index === current ? 'slide active' : 'slide'} key={slide?.id?.uuid || index}>
            {index === current && (
              <div
                data-elbaction={`click:click ${slide?.attributes?.title}-uuid-${slide?.id?.uuid}`}
                className={css.listingCardOuterWrapper}
              >
                <ListingCard
                  className={css.listingCard}
                  key={slide.id.uuid}
                  listing={slide}
                  renderSizes={cardRenderSizes}
                  setActiveListing={setActiveListing}
                  isSearchPage={isSearchPage}
                />
              </div>
            )}
          </div>
        );
      })}
      <div className={css.slider_btns}>
        <div className={css.slider__controls}>
          <button
            className={css.btn}
            onClick={prevSlide}
            data-elbaction={`click:click switchrtype-${activeSlide?.attributes?.title}`}
          >
            <img src={arroLeft} alt="Previous" />
          </button>
          <span className={css.counter}>
            {current + 1}/{length}
          </span>
          <button
            className={css.btn}
            onClick={nextSlide}
            data-elbaction={`click:click switchrtype-${activeSlide?.attributes?.title}`}
          >
            <img src={arroRight} alt="Next" />
          </button>
        </div>
      </div>
    </section>
  );
}
